import { Form } from '@edgebox/react-components';
import { ContentRepresentation, TaxonomyFacet } from '@edgebox/sync-core-data-definitions';
import { IGovernanceStatsFilters, ISyncCoreApi } from '@edgebox/sync-core-rest-client';
import React, { useContext, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { SyncCoreApiContext } from '../../../contexts/SyncCoreApiContext';
import { sendToParent } from '../../../frame-messages';
import { AddFilterDropdown, IInventoryFilterValue } from './Onboarding/ContentInventory/Components/AddFilterDropdown';
import { AnalyticsScreenSkeleton } from './Components/AnalyticsScreenSkeleton';
import { BrandPresencePageHeader } from './Components/BrandPresencePageHeader';
import { BP_CARD_CLASSES, BP_TOOLBAR_BUTTON_CLASSES } from './Components/brandPresenceClasses';
import { getCachedTaxonomies, getCachedTaxonomyTermsByFacet } from './Onboarding/ContentInventory/Components/useCachedTaxonomyData';
import { HealthCheckNameReference } from './Onboarding/ContentInventory/Components/HealthCheckNameReference';
import { loadHealthCheckReferenceDataByKey } from './Onboarding/ContentInventory/Components/HealthCheckReferenceData';
import { TaxonomyTermName } from './Onboarding/ContentInventory/Components/TaxonomyTermNameBadge';
import { GovernanceAnalyticsKpiCard } from './Components/GovernanceAnalyticsKpiCard';
import { GovernanceAnalyticsKpiCardRow } from './Components/GovernanceAnalyticsKpiCardRow';
import { GovernanceAnalyticsSectionCard } from './Components/GovernanceAnalyticsSectionCard';
import { GovernanceSegmentedSwitch } from './Components/GovernanceSegmentedSwitch';
import { TrendChartSkeleton } from './Components/BrandPresenceSkeleton';
import { ContentHealthTagSeries, ContentHealthTrendPeriod, ContentHealthTrendsChart } from './Components/ContentHealthTrendsChart';
import { ContentHealthMeterList, ContentHealthMeterRow } from './Components/ContentHealthMeterList';
import { ContentHealthCoverage } from './Components/ContentHealthCoverage';
import { ContentHealthInsightsRow, ContentHealthInsightsTable } from './Components/ContentHealthInsightsTable';
import {
  compareScoresAscending,
  ContentHealthFacetConfig,
  toScorePercentage,
  uniqueTermsByKey,
  weekStartLabel,
} from './Components/contentHealth.utils';
import { GAPS_REFRESH_FAILED_MESSAGE } from './Components/BrandLandscapeCard.utils';
import { KPIS_LOAD_FAILED_MESSAGE, SectionLoadFailed, TRENDS_LOAD_FAILED_MESSAGE, useRetry } from './Components/SectionLoadFailed';
import { scheduleStatsRequest, StatsRequestPriority, statsRequestKey } from './Components/statsRequestQueue';
import { brandPresenceIssueTypeIssuesPath, brandPresenceTaggedContentPath } from './brandPresencePaths';
import {
  searchOfBrandPresenceScreenAddress,
  toBrandPresenceSearch,
  useBrandPresenceScreenAddress,
  useReportBrandPresenceScreenQuery,
} from './brandPresenceScreenQuery';

/** The label that leads the filter bar. */
const FILTER_LABEL_CLASSES = 'font-eyebrow text-[0.625rem] font-medium uppercase tracking-[0.14em] text-slate-500';

/**
 * The header height of the two cards that stand side by side, Gaps and Quality issues: the height a
 * header holding a segmented switch has, so the one without a switch lines up with the one with it.
 */
const PAIRED_SECTION_HEADER_CLASSES = 'min-h-[4.3125rem]';

const HISTORY_BUCKETS = 6;
const QUERY_TERMS_PARAM = 'terms';
const NO_TERM_ASSIGNED_LABEL = 'None';
const NULL_FACET_TERM_KEY = '__facet_none__';
const ANALYTICS_PALETTE = ['#009EE3', '#FF5479', '#D96ECC', '#A237E0', '#7491ED', '#EE7402', '#3ABFF0', '#2CAC34'] as const;

const FACET_OPTIONS = [
  TaxonomyFacet.Brand,
  TaxonomyFacet.AuthorityTopic,
  TaxonomyFacet.Market,
  TaxonomyFacet.Product,
  TaxonomyFacet.Site,
] as const;

type AnalyticsFacet = (typeof FACET_OPTIONS)[number];
type DisplayMode = 'month' | 'week';
type OptionalFacet = AnalyticsFacet | 'none';
type AnalyticsContentType = ContentRepresentation;

/** What the screen counts for each content type, in the singular and the plural. */
const COUNT_NOUNS: Record<AnalyticsContentType, { one: string; many: string }> = {
  [ContentRepresentation.Page]: { one: 'page', many: 'pages' },
  [ContentRepresentation.Entity]: { one: 'entity', many: 'entities' },
  [ContentRepresentation.File]: { one: 'file', many: 'files' },
};

type TrendRow = {
  id: string;
  label: string;
  count: number;
  averageScore?: number;
  terms?: { term: string | null; count: number; averageScore?: number }[];
};

type IAnalyticsQuery = {
  selectedFilters: IInventoryFilterValue[];
};

/** What a headline figure reads while there is no figure: not loaded, or its request failed. */
const NO_FIGURE = '-';

function formatPercentage(value?: number): string {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return NO_FIGURE;
  }

  return `${Math.round(Math.max(0, Math.min(1, value)) * 100)}%`;
}

function formatCount(value?: number): string {
  return value === undefined ? NO_FIGURE : String(value);
}

function normalizeScore(score?: number | null): number | undefined {
  if (typeof score !== 'number' || !Number.isFinite(score)) {
    return undefined;
  }

  return Math.max(0, Math.min(1, score));
}

function buildStatsFilters(selectedFilters: IInventoryFilterValue[]): Pick<IGovernanceStatsFilters, 'taxonomyTerms'> {
  const taxonomyTerms = selectedFilters
    .filter((filter) => filter.targetType === 'taxonomy')
    .map((filter) => filter.valueId)
    .filter((term) => term.includes('.'));

  return {
    taxonomyTerms: taxonomyTerms.length ? taxonomyTerms : undefined,
  };
}

function serializeFiltersParam(filters: IInventoryFilterValue[]): string {
  return JSON.stringify(
    filters.map((filter) => ({
      targetId: filter.targetId,
      targetLabel: filter.targetLabel,
      targetType: filter.targetType,
      valueId: filter.valueId,
      valueLabel: filter.valueLabel,
    }))
  );
}

function coerceQueryStringValue(value: unknown): string | undefined {
  if (typeof value === 'string') {
    return value;
  }

  if (Array.isArray(value)) {
    const firstString = value.find((entry) => typeof entry === 'string');
    return typeof firstString === 'string' ? firstString : undefined;
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }

  return undefined;
}

function parseCsvValue(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value
      .flatMap((entry) => parseCsvValue(entry))
      .map((entry) => entry.trim())
      .filter(Boolean);
  }

  const serialized = coerceQueryStringValue(value);
  if (!serialized) {
    return [];
  }

  return serialized
    .split(',')
    .map((entry) => entry.trim())
    .filter(Boolean);
}

function toCsvValue(values: string[]): string | undefined {
  const deduped = Array.from(new Set(values.map((value) => value.trim()).filter(Boolean)));
  return deduped.length ? deduped.join(',') : undefined;
}

function readQueryParamFromSource(source: unknown, key: string): unknown {
  if (!source) {
    return undefined;
  }

  if (source instanceof URLSearchParams) {
    const values = source.getAll(key).filter((entry) => entry.trim().length > 0);
    if (!values.length) {
      return undefined;
    }

    return values.length === 1 ? values[0] : values;
  }

  if (typeof source === 'string') {
    const params = new URLSearchParams(source.replace(/^\?/, ''));
    const values = params.getAll(key).filter((entry) => entry.trim().length > 0);
    if (!values.length) {
      return undefined;
    }

    return values.length === 1 ? values[0] : values;
  }

  if (Array.isArray(source)) {
    for (const entry of source) {
      const value = readQueryParamFromSource(entry, key);
      if (value !== undefined) {
        return value;
      }
    }

    return undefined;
  }

  if (typeof source === 'object') {
    const record = source as Record<string, unknown>;
    const direct = record[key];
    if (direct !== undefined && direct !== null && !(typeof direct === 'string' && !direct.trim())) {
      return direct;
    }

    if (record.query !== undefined) {
      const nested = readQueryParamFromSource(record.query, key);
      if (nested !== undefined) {
        return nested;
      }
    }
  }

  return undefined;
}

function termLabelFromKey(value: string): string {
  const term = value.split('.').pop() || value;
  return term.replace(/[-_]+/g, ' ');
}

function buildFilterValuesFromQuery(taxonomyTermValues: string[]): IInventoryFilterValue[] {
  return taxonomyTermValues.map((value) => ({
    targetId: 'taxonomy',
    targetLabel: 'Taxonomy',
    targetType: 'taxonomy',
    valueId: value,
    valueLabel: termLabelFromKey(value),
  }));
}

/**
 * The filters as the host page's address carries them. Only that address is read: the frame's own
 * `location.search` is what a link put there once and nothing rewrites, so a filter removed from the
 * host page's address would come back from that stale copy on the next parse.
 */
function parseAnalyticsQuery(query: unknown, brandPresencePath?: string): IAnalyticsQuery {
  const pathParams = new URLSearchParams(brandPresencePath?.split('?')[1] || '');

  const readParam = (key: string): unknown => {
    const fromQuery = readQueryParamFromSource(query, key);
    if (fromQuery !== undefined) {
      return fromQuery;
    }

    const fromPath = pathParams.get(key);
    if (fromPath !== null && fromPath.trim()) {
      return fromPath;
    }

    return undefined;
  };

  const termsRaw = readParam(QUERY_TERMS_PARAM);
  const taxonomyTermValues = parseCsvValue(termsRaw);

  return {
    selectedFilters: taxonomyTermValues.length ? buildFilterValuesFromQuery(taxonomyTermValues) : [],
  };
}

function buildUrlQueryFromState(params: { selectedFilters: IInventoryFilterValue[] }): Record<string, string> {
  const taxonomyTermValues = params.selectedFilters
    .filter((filter) => filter.targetType === 'taxonomy')
    .map((filter) => filter.valueId)
    .filter((value) => value.includes('.'));

  return {
    ...(toCsvValue(taxonomyTermValues) ? { [QUERY_TERMS_PARAM]: toCsvValue(taxonomyTermValues) as string } : {}),
  };
}

function resolveTermKey(value: unknown): string | undefined {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed.length ? trimmed : undefined;
  }

  if (!value || typeof value !== 'object') {
    return undefined;
  }

  const candidate = value as { key?: unknown; value?: unknown; term?: unknown; id?: unknown; name?: unknown };
  const raw = candidate.key || candidate.value || candidate.term || candidate.id || candidate.name;
  return typeof raw === 'string' && raw.trim().length ? raw.trim() : undefined;
}

function resolveFacetTerm(value: unknown): string | null | undefined {
  if (value === null) {
    return null;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed.length) {
      return undefined;
    }

    if (trimmed.toLowerCase() === 'null') {
      return null;
    }

    return trimmed;
  }

  if (value && typeof value === 'object') {
    const candidate = value as { term?: unknown; value?: unknown; key?: unknown; id?: unknown; name?: unknown };

    if (candidate.term === null || candidate.value === null) {
      return null;
    }

    if (candidate.term !== undefined) {
      return resolveFacetTerm(candidate.term);
    }

    if (candidate.value !== undefined) {
      return resolveFacetTerm(candidate.value);
    }

    if (candidate.key !== undefined) {
      return resolveFacetTerm(candidate.key);
    }

    if (candidate.id !== undefined) {
      return resolveFacetTerm(candidate.id);
    }

    if (candidate.name !== undefined) {
      return resolveFacetTerm(candidate.name);
    }
  }

  return undefined;
}

function toAnalyticsContentType(value: string | undefined): AnalyticsContentType | undefined {
  if (value === ContentRepresentation.Page) {
    return ContentRepresentation.Page;
  }
  if (value === ContentRepresentation.Entity) {
    return ContentRepresentation.Entity;
  }
  if (value === ContentRepresentation.File) {
    return ContentRepresentation.File;
  }

  return undefined;
}

function toAnalyticsContentTypeLabel(value: AnalyticsContentType): string {
  if (value === ContentRepresentation.Page) {
    return 'Page';
  }
  if (value === ContentRepresentation.Entity) {
    return 'Entity';
  }

  return 'File';
}

/**
 * At or above this, a content-health bucket counts as fully healthy and is excluded from the
 * "needs attention" total.
 *
 * Deliberately its OWN constant, not one of the sentiment/attribute band edges it happens to share
 * a value with. Content health measures a document against its checks; the sentiment bands measure
 * how answers speak about a brand. They are re-derived from different data and must be free to move
 * independently — a shared constant here would silently drag one when the other was retuned.
 */
const CONTENT_HEALTH_FULLY_HEALTHY_AT_OR_ABOVE = 0.75;

function parseHealthBucketCount(total: { thresholds: { gte?: number | null; lt?: number | null; count: number }[] }): number {
  return total.thresholds.reduce((sum, threshold) => {
    const gte = threshold.gte ?? 0;
    const lt = threshold.lt ?? 1;
    const fullyHealthy = gte >= CONTENT_HEALTH_FULLY_HEALTHY_AT_OR_ABOVE && lt > CONTENT_HEALTH_FULLY_HEALTHY_AT_OR_ABOVE;

    return fullyHealthy ? sum : sum + threshold.count;
  }, 0);
}

function first3FacetTerms(rows: TrendRow[]): Array<string | null> {
  const countByTerm = new Map<string, number>();

  for (const row of rows) {
    for (const term of row.terms || []) {
      const bucketKey = term.term === null ? NULL_FACET_TERM_KEY : term.term;
      countByTerm.set(bucketKey, (countByTerm.get(bucketKey) || 0) + term.count);
    }
  }

  return Array.from(countByTerm.entries())
    .sort((left, right) => right[1] - left[1])
    .slice(0, 3)
    .map((entry) => (entry[0] === NULL_FACET_TERM_KEY ? null : entry[0]));
}

function toFacetLabelName(configs: ContentHealthFacetConfig<AnalyticsFacet>[], facet: AnalyticsFacet): string {
  return configs.find((config) => config.facet === facet)?.taxonomyName || facet;
}

function findConfiguredTerm(configs: ContentHealthFacetConfig<AnalyticsFacet>[], value: string) {
  for (const config of configs) {
    const matched = config.terms.find((term) => term.key === value || term.id === value || term.name === value);
    if (matched) {
      return matched;
    }
  }
  return undefined;
}

/**
 * Whether a row's term is one this screen can name.
 *
 * The facet term lists exclude archived terms; the stats endpoints deliberately do not, because the
 * server keeps archived aggregation rows so un-archiving a term surfaces its history at once. A row
 * keyed to an archived (or deleted) term therefore arrives with nothing to name it, and
 * `resolveTermName` would fall back to printing the raw key. Such rows are dropped instead: an
 * opaque key tells a reader no more than a blank does.
 */
function isDisplayableStatsTerm(configs: ContentHealthFacetConfig<AnalyticsFacet>[], value: string | null): boolean {
  // The explicit "no term assigned" bucket is a real, nameable category, not an unresolved term.
  return value === null || !!findConfiguredTerm(configs, value);
}

/** The kind of tag a key belongs to: its taxonomy where the screen knows it, else the key's own prefix. */
function taxonomyKeyOfTerm(configs: ContentHealthFacetConfig<AnalyticsFacet>[], termKey: string): string {
  return configs.find((config) => config.terms.some((term) => term.key === termKey))?.taxonomyKey ?? termKey.split('.')[0];
}

/**
 * The filter's tags as a Content Inventory link carries them, or nothing where no link can list what
 * the figures count. Sync Core counts an item that carries any one of two filter tags of the same
 * kind, while the list keeps only the items that carry all the tags it names.
 */
function inventoryFilterTermKeys(configs: ContentHealthFacetConfig<AnalyticsFacet>[], filterTermKeys: string[] = []): string[] | undefined {
  const termKeys = Array.from(new Set(filterTermKeys));
  const kinds = new Set(termKeys.map((termKey) => taxonomyKeyOfTerm(configs, termKey)));
  return kinds.size === termKeys.length ? termKeys : undefined;
}

function resolveTermName(configs: ContentHealthFacetConfig<AnalyticsFacet>[], value: string): string {
  return findConfiguredTerm(configs, value)?.name ?? value;
}

/**
 * A stats row's subject, as text — same rule as the AI Visibility tables: a row's identity is not
 * a chip, and status treatment still applies through the shared helper.
 */
function FacetTermName({ api, term }: { api?: ISyncCoreApi; term: string | null }) {
  if (term === null) {
    return <span className="text-slate-500">{NO_TERM_ASSIGNED_LABEL}</span>;
  }

  return <TaxonomyTermName api={api} termKey={term} context="table-row" />;
}

function periodLabelOf(entry: { year: number; month?: number; timestamp: number }, displayMode: DisplayMode): string {
  if (displayMode === 'week') {
    return weekStartLabel(entry.timestamp);
  }

  return `${entry.month}/${String(entry.year).slice(-2)}`;
}

export function GovernanceAnalyticsContentHealth() {
  const navigate = useNavigate();
  const { type: routeType } = useParams<{ type?: string }>();
  const apiContext = useContext(SyncCoreApiContext);
  const api = apiContext?.api;
  const screenAddress = useBrandPresenceScreenAddress();
  const { query: requestedQuery, path: requestedPath } = screenAddress;
  const parsedAnalyticsQuery = useMemo(() => parseAnalyticsQuery(requestedQuery, requestedPath), [requestedQuery, requestedPath]);
  const screenSearch = searchOfBrandPresenceScreenAddress(screenAddress);
  const parsedAnalyticsQuerySerialized = useMemo(() => JSON.stringify(parsedAnalyticsQuery), [parsedAnalyticsQuery]);
  const lastSentQuerySerializedRef = React.useRef<string | undefined>(undefined);
  const isHydratedFromIncomingRef = React.useRef(false);
  const initialHydratedQuerySerializedRef = React.useRef<string | undefined>(undefined);

  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [loadAttempt, retryLoad] = useRetry();
  const [selectedFilters, setSelectedFilters] = useState<IInventoryFilterValue[]>(() => parsedAnalyticsQuery.selectedFilters);
  const [displayMode, setDisplayMode] = useState<DisplayMode>('month');
  const [facetConfigs, setFacetConfigs] = useState<ContentHealthFacetConfig<AnalyticsFacet>[]>([]);

  const [trendsFacet, setTrendsFacet] = useState<OptionalFacet>('none');
  const [trendsRows, setTrendsRows] = useState<TrendRow[]>([]);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryFailed, setSummaryFailed] = useState(false);
  const [summaryAttempt, retrySummary] = useRetry();
  const [trendsLoading, setTrendsLoading] = useState(false);
  const [trendsFailed, setTrendsFailed] = useState(false);
  const [trendsAttempt, retryTrends] = useRetry();

  const [gapsFacet, setGapsFacet] = useState<AnalyticsFacet | undefined>(undefined);
  const [gapsRows, setGapsRows] = useState<{ term: string | null; count: number; averageScore?: number }[]>([]);
  const [gapsLoading, setGapsLoading] = useState(false);
  const [gapsFailed, setGapsFailed] = useState(false);
  const [gapsAttempt, retryGaps] = useRetry();

  const [qualityRows, setQualityRows] = useState<{ key: string; count: number; averageScore: number }[]>([]);
  const [qualityLoading, setQualityLoading] = useState(false);
  const [qualityFailed, setQualityFailed] = useState(false);
  const [qualityAttempt, retryQuality] = useRetry();
  const [issueTypeByCheck, setIssueTypeByCheck] = useState<Record<string, string>>({});

  const [coverageLoading, setCoverageLoading] = useState(false);

  const [insightsFacet, setInsightsFacet] = useState<AnalyticsFacet | ''>('');
  const [insightsRows, setInsightsRows] = useState<{ term: string | null; averageScore?: number; checks: Record<string, number> }[]>([]);
  const [insightsIssueTypes, setInsightsIssueTypes] = useState<string[]>([]);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [insightsFailed, setInsightsFailed] = useState(false);
  const [insightsAttempt, retryInsights] = useRetry();
  // Nothing until the figures arrive: a count of 0 would read as a result.
  const [totalAverageScore, setTotalAverageScore] = useState<number | undefined>(undefined);
  const [atRiskCount, setAtRiskCount] = useState<number | undefined>(undefined);
  const [criticalFailuresCount, setCriticalFailuresCount] = useState<number | undefined>(undefined);
  const [totalCount, setTotalCount] = useState<number | undefined>(undefined);

  const statsFilters = useMemo(() => buildStatsFilters(selectedFilters), [selectedFilters]);
  const selectedFiltersSerialized = useMemo(() => serializeFiltersParam(selectedFilters), [selectedFilters]);
  const statsType = useMemo(() => toAnalyticsContentType(routeType) || ContentRepresentation.Page, [routeType]);
  const countNouns = COUNT_NOUNS[statsType];

  /**
   * The Content Inventory address that lists exactly what a figure counts: the items of the type in
   * view that carry the filter's tags and the figure's own, or nothing where the list cannot.
   */
  const linkFilterTermKeys = useMemo(
    () => inventoryFilterTermKeys(facetConfigs, statsFilters.taxonomyTerms),
    [facetConfigs, statsFilters.taxonomyTerms]
  );
  const contentPathFor = React.useCallback(
    (termKeys: string[]) =>
      linkFilterTermKeys ? brandPresenceTaggedContentPath({ type: statsType, termKeys: [...linkFilterTermKeys, ...termKeys] }) : undefined,
    [linkFilterTermKeys, statsType]
  );

  React.useEffect(() => {
    if (toAnalyticsContentType(routeType)) {
      return;
    }

    // The one address's search, not the frame's own: a type filled in must keep the filters the
    // host page's address carries, which the frame's stale copy may lack or contradict.
    navigate(`/brand-presence/analytics/content-health/${ContentRepresentation.Page}${screenSearch}`, { replace: true });
  }, [screenSearch, navigate, routeType]);

  React.useEffect(() => {
    const parsedFiltersSerialized = serializeFiltersParam(parsedAnalyticsQuery.selectedFilters);

    if (parsedFiltersSerialized !== selectedFiltersSerialized) {
      setSelectedFilters(parsedAnalyticsQuery.selectedFilters);
    }

    const hydratedQuery = buildUrlQueryFromState({
      selectedFilters: parsedAnalyticsQuery.selectedFilters,
    });
    const hydratedSerialized = JSON.stringify(hydratedQuery);
    initialHydratedQuerySerializedRef.current = hydratedSerialized;
    lastSentQuerySerializedRef.current = hydratedSerialized;
    isHydratedFromIncomingRef.current = true;
  }, [parsedAnalyticsQuerySerialized]);

  React.useEffect(() => {
    if (!isHydratedFromIncomingRef.current) {
      return;
    }

    const nextQuery = buildUrlQueryFromState({
      selectedFilters,
    });

    const nextSerialized = JSON.stringify(nextQuery);

    if (initialHydratedQuerySerializedRef.current === nextSerialized && lastSentQuerySerializedRef.current === nextSerialized) {
      return;
    }

    if (lastSentQuerySerializedRef.current === nextSerialized) {
      return;
    }

    lastSentQuerySerializedRef.current = nextSerialized;
    sendToParent({
      type: 'update-query',
      query: nextQuery,
    });
  }, [selectedFiltersSerialized]);

  const screenQuery = useMemo(() => buildUrlQueryFromState({ selectedFilters }), [selectedFilters]);
  useReportBrandPresenceScreenQuery(screenQuery);

  React.useEffect(() => {
    if (!api) {
      setLoading(false);
      return;
    }

    let active = true;
    setLoading(true);
    setLoadFailed(false);
    void (async () => {
      try {
        const taxonomies = await getCachedTaxonomies(api);
        const nextConfigs: ContentHealthFacetConfig<AnalyticsFacet>[] = [];

        for (const facet of FACET_OPTIONS) {
          const taxonomy = taxonomies.find((entry) => entry.facet === facet);
          if (!taxonomy) {
            continue;
          }

          const terms = await getCachedTaxonomyTermsByFacet(api, facet);
          if (!terms.length) {
            continue;
          }

          nextConfigs.push({
            facet,
            taxonomyKey: String(taxonomy.key || ''),
            taxonomyName: String(taxonomy.name || facet),
            // One entry per key: a tag with revisions at several statuses comes back once per revision.
            terms: uniqueTermsByKey(
              terms
                .map((term) => ({ id: term.id, key: String(term.key || ''), name: String(term.name || '').trim(), status: term.status }))
                .filter((term) => term.key && term.name)
            ),
          });
        }

        if (!active) {
          return;
        }

        setFacetConfigs(nextConfigs);
        setGapsFacet(nextConfigs[0]?.facet);
      } catch {
        // Every section is broken down by these tags, so without them the screen has nothing to show.
        // The lists that did load stay cached, so trying again asks only for the rest.
        if (active) {
          setLoadFailed(true);
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    })();

    return () => {
      active = false;
    };
  }, [api, loadAttempt]);

  React.useEffect(() => {
    if (!api || !facetConfigs.length) {
      setSummaryLoading(false);
      setSummaryFailed(false);
      return;
    }

    let active = true;
    // Aborted when the filter changes or the screen is left: a request not sent yet is then dropped,
    // and one on its way no longer holds a place for the requests of the new filter.
    const superseded = new AbortController();

    void (async () => {
      setSummaryLoading(true);
      setSummaryFailed(false);
      try {
        const [total, checks] = await Promise.all([
          scheduleStatsRequest({
            key: statsRequestKey({ method: 'contentStats.total', parameters: [statsType, statsFilters] }),
            priority: StatsRequestPriority.ContentHealthKpis,
            signal: superseded.signal,
            run: () => api.governance.stats.total(statsType, statsFilters),
          }),
          scheduleStatsRequest({
            key: statsRequestKey({ method: 'contentStats.checks', parameters: [statsType, statsFilters] }),
            priority: StatsRequestPriority.ContentHealthKpis,
            signal: superseded.signal,
            run: () => api.governance.stats.checks(statsType, statsFilters),
          }),
        ]);

        if (!active) {
          return;
        }

        setTotalAverageScore(normalizeScore(total.averageScore));
        setAtRiskCount(parseHealthBucketCount(total));
        setTotalCount(total.count);
        setCriticalFailuresCount(
          (checks.items || [])
            .filter((item) => String(item.criticality || '').toLowerCase() === 'critical')
            .reduce((sum, item) => sum + item.count, 0)
        );
      } catch {
        // Without one read the cards cannot be filled, so the other, if still waiting for its turn, is
        // dropped rather than sent for nothing.
        superseded.abort();
        // The figures on the cards belong to the filter that last succeeded, so they are cleared
        // rather than left to read as this one's.
        if (active) {
          setTotalAverageScore(undefined);
          setAtRiskCount(undefined);
          setTotalCount(undefined);
          setCriticalFailuresCount(undefined);
          setSummaryFailed(true);
        }
      } finally {
        if (active) {
          setSummaryLoading(false);
        }
      }
    })();

    return () => {
      active = false;
      superseded.abort();
    };
  }, [api, facetConfigs, statsFilters, statsType, summaryAttempt]);

  React.useEffect(() => {
    if (!api || !facetConfigs.length) {
      setTrendsLoading(false);
      setTrendsFailed(false);
      return;
    }

    let active = true;
    // See the headline figures above.
    const superseded = new AbortController();

    void (async () => {
      setTrendsLoading(true);
      setTrendsFailed(false);
      try {
        const selectedFacetConfig = trendsFacet === 'none' ? undefined : facetConfigs.find((entry) => entry.facet === trendsFacet);
        const filters = {
          ...statsFilters,
          ...(selectedFacetConfig ? { facetTaxonomyKey: [selectedFacetConfig.taxonomyKey] } : {}),
        };

        const history =
          displayMode === 'month'
            ? await scheduleStatsRequest({
                key: statsRequestKey({ method: 'contentStats.monthlyHistory', parameters: [statsType, HISTORY_BUCKETS, filters] }),
                priority: StatsRequestPriority.ContentHealthTrends,
                signal: superseded.signal,
                run: () => api.governance.stats.monthlyHistory(statsType, HISTORY_BUCKETS, filters),
              })
            : await scheduleStatsRequest({
                key: statsRequestKey({ method: 'contentStats.weeklyHistory', parameters: [statsType, HISTORY_BUCKETS, filters] }),
                priority: StatsRequestPriority.ContentHealthTrends,
                signal: superseded.signal,
                run: () => api.governance.stats.weeklyHistory(statsType, HISTORY_BUCKETS, filters),
              });

        if (!active) {
          return;
        }

        const rows = history.map((entry) => ({
          id: entry.identifier,
          label: periodLabelOf(entry, displayMode),
          count: entry.count,
          averageScore: normalizeScore(entry.averageScore),
          terms: (entry.terms || [])
            .map((term) => ({
              term: resolveFacetTerm(term.term),
              count: term.count,
              averageScore: normalizeScore(term.averageScore),
            }))
            .filter((term) => term.term !== undefined && isDisplayableStatsTerm(facetConfigs, term.term as string | null)) as {
            term: string | null;
            count: number;
            averageScore?: number;
          }[],
        }));

        setTrendsRows(rows);
      } catch {
        // The periods on the chart belong to the selection that last succeeded, not to this one.
        if (active) {
          setTrendsRows([]);
          setTrendsFailed(true);
        }
      } finally {
        if (active) {
          setTrendsLoading(false);
        }
      }
    })();

    return () => {
      active = false;
      superseded.abort();
    };
  }, [api, displayMode, facetConfigs, statsFilters, statsType, trendsFacet, trendsAttempt]);

  React.useEffect(() => {
    if (!api || !gapsFacet || !facetConfigs.length) {
      setGapsLoading(false);
      setGapsFailed(false);
      return;
    }

    let active = true;
    // See the headline figures above.
    const superseded = new AbortController();
    void (async () => {
      setGapsLoading(true);
      setGapsFailed(false);
      try {
        const facetConfig = facetConfigs.find((entry) => entry.facet === gapsFacet);
        if (!facetConfig) {
          return;
        }

        const filters = { ...statsFilters, facetTaxonomyKey: [facetConfig.taxonomyKey] };
        const result = await scheduleStatsRequest({
          key: statsRequestKey({ method: 'contentStats.total', parameters: [statsType, filters] }),
          priority: StatsRequestPriority.ContentHealthGaps,
          signal: superseded.signal,
          run: () => api.governance.stats.total(statsType, filters),
        });

        if (!active) {
          return;
        }

        // Worst first: the tags that need attention lead the list.
        const sortedRows = (result.terms || [])
          .map((entry) => ({ term: resolveFacetTerm(entry.term), count: entry.count, averageScore: normalizeScore(entry.averageScore) }))
          .filter((entry) => entry.term !== undefined && isDisplayableStatsTerm(facetConfigs, entry.term as string | null))
          .sort((left, right) => compareScoresAscending(left.averageScore, right.averageScore) || right.count - left.count);

        setGapsRows(sortedRows as { term: string | null; count: number; averageScore?: number }[]);
      } catch {
        // The rows belong to the selection that last succeeded, not to this one.
        if (active) {
          setGapsRows([]);
          setGapsFailed(true);
        }
      } finally {
        if (active) {
          setGapsLoading(false);
        }
      }
    })();

    return () => {
      active = false;
      superseded.abort();
    };
  }, [api, facetConfigs, gapsFacet, statsFilters, statsType, gapsAttempt]);

  React.useEffect(() => {
    if (!api || !facetConfigs.length) {
      setQualityLoading(false);
      setQualityFailed(false);
      return;
    }

    let active = true;
    // See the headline figures above. The headline figures ask for the same checks, so the two
    // sections share the one request.
    const superseded = new AbortController();
    void (async () => {
      setQualityLoading(true);
      setQualityFailed(false);
      try {
        const result = await scheduleStatsRequest({
          key: statsRequestKey({ method: 'contentStats.checks', parameters: [statsType, statsFilters] }),
          priority: StatsRequestPriority.ContentHealthQualityIssues,
          signal: superseded.signal,
          run: () => api.governance.stats.checks(statsType, statsFilters),
        });

        if (!active) {
          return;
        }

        const rows = (result.items || [])
          .map((item) => ({
            key: item.healthCheckKey,
            count: item.count,
            averageScore: normalizeScore(item.averageScore) || 0,
          }))
          .sort((left, right) => left.averageScore - right.averageScore || right.count - left.count);

        setQualityRows(rows);
      } catch {
        // The rows belong to the selection that last succeeded, not to this one.
        if (active) {
          setQualityRows([]);
          setQualityFailed(true);
        }
      } finally {
        if (active) {
          setQualityLoading(false);
        }
      }
    })();

    return () => {
      active = false;
      superseded.abort();
    };
  }, [api, facetConfigs, statsFilters, statsType, qualityAttempt]);

  // The issue type a check raises is what the issue list filters by.
  React.useEffect(() => {
    if (!api || !qualityRows.length) {
      return;
    }

    let active = true;
    void Promise.all(
      qualityRows.map(async (row) => [row.key, (await loadHealthCheckReferenceDataByKey(api, row.key)).issueTypeKeys[0]] as const)
    ).then((entries) => {
      if (active) {
        setIssueTypeByCheck(
          Object.fromEntries(entries.filter((entry): entry is readonly [string, string] => typeof entry[1] === 'string'))
        );
      }
    });

    return () => {
      active = false;
    };
  }, [api, qualityRows]);

  React.useEffect(() => {
    if (!api || !facetConfigs.length || !insightsFacet) {
      setInsightsRows([]);
      setInsightsIssueTypes([]);
      setInsightsLoading(false);
      setInsightsFailed(false);
      return;
    }

    let active = true;
    // See the headline figures above.
    const superseded = new AbortController();

    void (async () => {
      setInsightsLoading(true);
      setInsightsFailed(false);
      try {
        const selectedFacetConfig = facetConfigs.find((entry) => entry.facet === insightsFacet);
        if (!selectedFacetConfig) {
          return;
        }

        const termsFilters = { ...statsFilters, facetTaxonomyKey: [selectedFacetConfig.taxonomyKey] };
        const termsResult = await scheduleStatsRequest({
          key: statsRequestKey({ method: 'contentStats.total', parameters: [statsType, termsFilters] }),
          priority: StatsRequestPriority.ContentHealthInsights,
          signal: superseded.signal,
          run: () => api.governance.stats.total(statsType, termsFilters),
        });

        const termEntries = (termsResult.terms || [])
          .map((term) => ({ term: resolveFacetTerm(term.term), averageScore: normalizeScore(term.averageScore) }))
          .filter((term) => term.term !== undefined) as Array<{ term: string | null; averageScore?: number }>;

        const checkResults = await Promise.all(
          termEntries.map(async (entry) => {
            if (entry.term === null) {
              return {
                term: entry.term,
                averageScore: entry.averageScore,
                checks: [],
              };
            }

            const termFilters = { ...statsFilters, taxonomyTerms: [...(statsFilters.taxonomyTerms || []), entry.term] };
            const checks = await scheduleStatsRequest({
              key: statsRequestKey({ method: 'contentStats.checks', parameters: [statsType, termFilters] }),
              priority: StatsRequestPriority.ContentHealthInsights,
              signal: superseded.signal,
              run: () => api.governance.stats.checks(statsType, termFilters),
            });

            return {
              term: entry.term,
              averageScore: entry.averageScore,
              checks: checks.items || [],
            };
          })
        );

        if (!active) {
          return;
        }

        const issueTypes = Array.from(
          new Set(checkResults.flatMap((entry) => entry.checks.map((check) => check.healthCheckKey)).filter((value) => value.length > 0))
        );

        setInsightsIssueTypes(issueTypes);
        setInsightsRows(
          checkResults.map((entry) => ({
            term: entry.term,
            averageScore: entry.averageScore,
            checks: Object.fromEntries(entry.checks.map((check) => [check.healthCheckKey, normalizeScore(check.averageScore) || 0])),
          }))
        );
      } catch {
        // Without every tag's checks the table cannot be filled, so the reads still waiting for their
        // turn are dropped rather than sent for nothing. The rows belong to the selection that last
        // succeeded, not to this one.
        superseded.abort();
        if (active) {
          setInsightsRows([]);
          setInsightsIssueTypes([]);
          setInsightsFailed(true);
        }
      } finally {
        if (active) {
          setInsightsLoading(false);
        }
      }
    })();

    return () => {
      active = false;
      superseded.abort();
    };
  }, [api, facetConfigs, insightsFacet, statsFilters, statsType, insightsAttempt]);

  React.useEffect(() => {
    if (!facetConfigs.length) {
      setInsightsFacet('');
      return;
    }

    setInsightsFacet((current) => {
      if (current && facetConfigs.some((config) => config.facet === current)) {
        return current;
      }

      return facetConfigs[0].facet;
    });
  }, [facetConfigs]);

  const trendPeriods: ContentHealthTrendPeriod[] = useMemo(
    () =>
      trendsRows.map((row) => ({
        id: row.id,
        label: row.label,
        count: row.count,
        health: row.count ? toScorePercentage(row.averageScore) : undefined,
      })),
    [trendsRows]
  );

  const trendTagSeries: ContentHealthTagSeries[] | undefined = useMemo(() => {
    if (trendsFacet === 'none') {
      return undefined;
    }

    return first3FacetTerms(trendsRows)
      .filter((term) => isDisplayableStatsTerm(facetConfigs, term))
      .map((term, index) => ({
        key: term ?? NULL_FACET_TERM_KEY,
        label: term === null ? NO_TERM_ASSIGNED_LABEL : resolveTermName(facetConfigs, term),
        color: ANALYTICS_PALETTE[index % ANALYTICS_PALETTE.length],
        values: trendsRows.map((row) => {
          const matched = row.terms?.find((entry) => entry.term === term);
          return { count: matched?.count || 0, health: matched?.count ? toScorePercentage(matched.averageScore) : undefined };
        }),
      }));
  }, [facetConfigs, trendsFacet, trendsRows]);

  const gapsMeterRows: ContentHealthMeterRow[] = useMemo(
    () =>
      gapsRows.map((row) => ({
        id: row.term ?? NULL_FACET_TERM_KEY,
        name: <FacetTermName api={api} term={row.term} />,
        count: row.count,
        score: row.averageScore,
        to: row.term === null ? undefined : contentPathFor([row.term]),
        linkHint: 'Show in Content Inventory',
      })),
    [api, contentPathFor, gapsRows]
  );

  const qualityMeterRows: ContentHealthMeterRow[] = useMemo(
    () =>
      qualityRows.map((row) => ({
        id: row.key,
        name: <HealthCheckNameReference api={api} healthCheckKey={row.key} fallback={row.key} />,
        count: row.count,
        score: row.averageScore,
        to: issueTypeByCheck[row.key] ? brandPresenceIssueTypeIssuesPath({ issueTypeKey: issueTypeByCheck[row.key] }) : undefined,
        linkHint: 'Show in Issues',
      })),
    [api, issueTypeByCheck, qualityRows]
  );

  const insightsTableRows: ContentHealthInsightsRow[] = useMemo(
    () =>
      insightsRows.map((row) => ({
        term: row.term,
        name: <FacetTermName api={api} term={row.term} />,
        nameText: row.term === null ? NO_TERM_ASSIGNED_LABEL : resolveTermName(facetConfigs, row.term),
        averageScore: row.averageScore,
        checks: row.checks,
        to: row.term === null ? undefined : contentPathFor([row.term]),
      })),
    [api, contentPathFor, facetConfigs, insightsRows]
  );

  const checkScores = useMemo(() => Object.fromEntries(qualityRows.map((row) => [row.key, row.averageScore])), [qualityRows]);

  if (loading) {
    return <AnalyticsScreenSkeleton label="Loading Content Health" kpiCount={4} withTabs={false} layout="content-health" />;
  }

  const pageHeader = (
    <BrandPresencePageHeader
      eyebrow={
        <>
          Brand Presence <span className="text-slate-300">/</span> Analytics
        </>
      }
      title="Content Health"
      description="How well your content is set up to be found and quoted: its quality over time, where it falls short, and which topics it covers."
    />
  );

  if (loadFailed) {
    return (
      <div className="relative w-full h-full bg-slate-50 text-slate-900 bg-brand-page">
        <main className="relative max-w-[1800px] mx-auto px-6 py-8">
          {pageHeader}
          <SectionLoadFailed message={GAPS_REFRESH_FAILED_MESSAGE} onRetry={retryLoad} />
        </main>
      </div>
    );
  }

  return (
    <div className="relative w-full h-full bg-slate-50 text-slate-900 bg-brand-page">
      <main className="relative max-w-[1800px] min-h-[800px] mx-auto px-6 py-8">
        {pageHeader}
        <div className={`${BP_CARD_CLASSES} mb-6 px-4 py-3 motion-safe:animate-bp-fade-up`}>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
              <span className={FILTER_LABEL_CLASSES}>Filters</span>
              <div className="d-flex align-items-center gap-2">
                <span className="text-sm font-semibold text-slate-600">Type</span>
                <Form.Select
                  size="sm"
                  value={statsType}
                  style={{ minWidth: '150px' }}
                  onChange={(event) => {
                    const nextType = toAnalyticsContentType(event.target.value) || ContentRepresentation.Page;
                    // The screen's live filters travel with the move, not the frame's own search —
                    // that is the address of an earlier arrival and may hold a filter since removed.
                    navigate(`/brand-presence/analytics/content-health/${nextType}${toBrandPresenceSearch(screenQuery)}`);
                  }}
                >
                  {[ContentRepresentation.Page, ContentRepresentation.Entity, ContentRepresentation.File].map((value) => (
                    <option key={value} value={value}>
                      {toAnalyticsContentTypeLabel(value)}
                    </option>
                  ))}
                </Form.Select>
              </div>
              <AddFilterDropdown
                api={api}
                selectedFilters={selectedFilters}
                onChange={setSelectedFilters}
                allowedTargetTypes={['taxonomy']}
              />
            </div>
            <button type="button" className={BP_TOOLBAR_BUTTON_CLASSES} onClick={() => setSelectedFilters([])}>
              Reset filters
            </button>
          </div>
        </div>

        {summaryFailed ? (
          <div className="mb-4">
            <SectionLoadFailed message={KPIS_LOAD_FAILED_MESSAGE} onRetry={retrySummary} />
          </div>
        ) : null}
        <GovernanceAnalyticsKpiCardRow>
          <GovernanceAnalyticsKpiCard title="Avg Quality" value={formatPercentage(totalAverageScore)} loading={summaryLoading} />
          <GovernanceAnalyticsKpiCard title="At-risk content" value={formatCount(atRiskCount)} loading={summaryLoading} />
          <GovernanceAnalyticsKpiCard title="Critical check failures" value={formatCount(criticalFailuresCount)} loading={summaryLoading} />
          <GovernanceAnalyticsKpiCard
            title="Content items"
            value={formatCount(totalCount)}
            subtitle={`${HISTORY_BUCKETS} ${displayMode === 'month' ? 'months' : 'weeks'} window`}
            loading={summaryLoading}
          />
        </GovernanceAnalyticsKpiCardRow>

        <div className="space-y-6">
          <GovernanceAnalyticsSectionCard
            title="Trends"
            loading={trendsLoading}
            actions={
              <>
                <GovernanceSegmentedSwitch
                  value={trendsFacet}
                  onChange={setTrendsFacet}
                  options={[
                    { value: 'none', label: 'Combined' },
                    ...facetConfigs.map((config) => ({ value: config.facet, label: config.taxonomyName })),
                  ]}
                />
                <GovernanceSegmentedSwitch
                  value={displayMode}
                  onChange={setDisplayMode}
                  options={[
                    { value: 'month', label: 'Month' },
                    { value: 'week', label: 'Week' },
                  ]}
                />
              </>
            }
          >
            {trendsFailed ? (
              <SectionLoadFailed message={TRENDS_LOAD_FAILED_MESSAGE} onRetry={retryTrends} />
            ) : trendsLoading && !trendsRows.length ? (
              <TrendChartSkeleton />
            ) : (
              <ContentHealthTrendsChart
                periods={trendPeriods}
                tagSeries={trendTagSeries}
                countNoun={countNouns.many}
                periodNoun={displayMode === 'month' ? 'months' : 'weeks'}
              />
            )}
          </GovernanceAnalyticsSectionCard>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <GovernanceAnalyticsSectionCard
              title="Gaps"
              loading={gapsLoading}
              headerClassName={PAIRED_SECTION_HEADER_CLASSES}
              actions={
                gapsFacet ? (
                  <GovernanceSegmentedSwitch
                    value={gapsFacet}
                    onChange={setGapsFacet}
                    options={facetConfigs.map((config) => ({ value: config.facet, label: config.taxonomyName }))}
                  />
                ) : null
              }
            >
              {gapsFailed ? (
                <SectionLoadFailed message={GAPS_REFRESH_FAILED_MESSAGE} onRetry={retryGaps} />
              ) : (
                <ContentHealthMeterList
                  key={gapsFacet}
                  rows={gapsMeterRows}
                  countNoun={countNouns.many}
                  singularCountNoun={countNouns.one}
                  emptyMessage={`No ${countNouns.one} carries a tag of this kind yet.`}
                  loading={gapsLoading}
                />
              )}
            </GovernanceAnalyticsSectionCard>

            <GovernanceAnalyticsSectionCard title="Quality issues" loading={qualityLoading} headerClassName={PAIRED_SECTION_HEADER_CLASSES}>
              {qualityFailed ? (
                <SectionLoadFailed message={GAPS_REFRESH_FAILED_MESSAGE} onRetry={retryQuality} />
              ) : (
                <ContentHealthMeterList
                  rows={qualityMeterRows}
                  countNoun={countNouns.many}
                  singularCountNoun={countNouns.one}
                  emptyMessage={`No ${countNouns.one} has been checked yet.`}
                  loading={qualityLoading}
                />
              )}
            </GovernanceAnalyticsSectionCard>
          </div>

          <GovernanceAnalyticsSectionCard
            title="Coverage"
            description={`How many ${countNouns.many} carry both tags, shaded by their average health.`}
            loading={coverageLoading}
          >
            <ContentHealthCoverage
              api={api}
              facets={facetConfigs}
              statsType={statsType}
              statsFilters={statsFilters}
              countNoun={countNouns.many}
              singularCountNoun={countNouns.one}
              contentPathFor={contentPathFor}
              onLoadingChange={setCoverageLoading}
            />
          </GovernanceAnalyticsSectionCard>

          <GovernanceAnalyticsSectionCard
            title="Insights"
            description={`Each figure is the average score a check gives the ${countNouns.many} with that tag; lower means more of them fall short.`}
            loading={insightsLoading}
            actions={
              facetConfigs.length ? (
                <GovernanceSegmentedSwitch
                  value={insightsFacet || facetConfigs[0].facet}
                  onChange={(value) => setInsightsFacet(value as AnalyticsFacet)}
                  options={facetConfigs.map((config) => ({ value: config.facet, label: config.taxonomyName }))}
                />
              ) : null
            }
          >
            {!insightsFacet ? (
              <div className="text-sm text-slate-500">Select a facet to view health insights.</div>
            ) : insightsFailed ? (
              <SectionLoadFailed message={GAPS_REFRESH_FAILED_MESSAGE} onRetry={retryInsights} />
            ) : (
              <ContentHealthInsightsTable
                api={api}
                facetName={toFacetLabelName(facetConfigs, insightsFacet)}
                rows={insightsTableRows}
                checkKeys={insightsIssueTypes}
                checkScores={checkScores}
                emptyMessage={`No ${countNouns.one} carries a tag of this kind yet.`}
                loading={insightsLoading}
              />
            )}
          </GovernanceAnalyticsSectionCard>
        </div>
      </main>
    </div>
  );
}
